import { SpecialLayoutObject } from '@/app/(app)/(special-layout)/special-layout-objects'
import { Campaign as CampaignObject, Legacyprofile, Media, Publicprofile, FeaturedProfiles as FeaturedProfilesData } from '@/payload-types'
import { Home as HomeData } from '@/payload-types'
import { IGraphQLGlobalResponse } from '@/types/graphql'
import { getGlobalSpecificFields } from './fetchDataFromPayload'
import { Home } from '@/payload-types'
import homePageDataFields from './queryFields/homePageQueryFields'
import { CANARY_MISSION } from '@/enum/enum'
import { isVideoMedia } from '@/utils/utils'
import { TypesenseData } from '@/lib/typesenseFunction'
import { getPostData } from './postFunctions'
import { homePageCampaignDataFields } from './queryFields/homePageQueryFields'
export async function getHomePageData(isDraft: boolean) {
  const data: IGraphQLGlobalResponse<Home, 'Home'> = (await getGlobalSpecificFields(
    'Home',
    homePageDataFields,
    isDraft,
  )) as IGraphQLGlobalResponse<Home, 'Home'>
  return data.Home
}

export interface IProfileBlock {
  profile?:
  | (
    | {
      relationTo: 'legacyprofiles'
      value: string | Legacyprofile
    }
    | {
      relationTo: 'publicprofiles'
      value: string | Publicprofile
    }
  )[]
  | null
}

export type HasManyProfileList =
  | (
    | {
      relationTo: 'legacyprofiles'
      value: string | Legacyprofile
    }
    | {
      relationTo: 'publicprofiles'
      value: string | Publicprofile
    }
  )[]
  | null
  | undefined

export interface IProfileLink {
  slug: string
  fullName: string
  profileImage: string
}

interface IFeaturedCampaignsData {
  data?: SpecialLayoutObject
  profileList?: IProfileLink[]
}

export const fetchCampaignData = async (
  featuredCampaigns: HomeData['featuredCampaigns']
) => {
  const featuredCampaignsData: IFeaturedCampaignsData[] = [];

  for (const campaign of featuredCampaigns || []) {
    const slug = typeof campaign?.campaign === "string" ? campaign?.campaign : campaign?.campaign?.slug || ""
    const campaignData: CampaignObject = await getPostData<CampaignObject>('Campaigns', slug, false, homePageCampaignDataFields)

    const profileList: IProfileLink[] = [];

    for (const block of campaignData.block || []) {
      switch (block.blockType) {
        case "keyProfileList":
        case "regularProfileList":
        case "compactProfileList": {
          const shouldBreak = await processProfilesFromBlock(
            block.hasManyProfileList as HasManyProfileList,
            profileList
          );
          if (shouldBreak) break;
          continue;
        }

        case "profileList": {
          const splitProfiles = (isKey: boolean) =>
            block.profileList
              ?.filter((p) => isKey ? p.keyProfile === true : !p.keyProfile)
              .map((p) => p.profile)
              .filter((p): p is NonNullable<typeof p> => p !== null) ?? [];

          // First key profiles, then non-key
          const profileGroups = [splitProfiles(true), splitProfiles(false)];

          for (const group of profileGroups) {
            const shouldBreak = await processProfilesFromBlock(
              group as HasManyProfileList,
              profileList
            );
            if (shouldBreak) break;
          }
          continue;
        }
      }
    }


    const iFrameType = isVideoMedia(campaign.campaignMedia as Media) ? 'Video' : 'Image'
    let iFrameSrc
    let coverImage = null

    if (campaign.campaignMedia) {
      if (iFrameType === 'Video') {
        const video = campaign.campaignMedia as Media
        iFrameSrc = `${process.env.NEXT_PUBLIC_PAYLOAD_URL}${video.url}`
        coverImage = `${process.env.NEXT_PUBLIC_PAYLOAD_URL}${typeof video.coverImage === 'string' ? video.coverImage : video.coverImage?.sizes?.medium?.url ?? ''
          }`
      } else {
        const image = campaign.campaignMedia as Media
        iFrameSrc = `${process.env.NEXT_PUBLIC_PAYLOAD_URL}${image.sizes?.medium?.url}`
      }
    } else {
      iFrameSrc = `${process.env.NEXT_PUBLIC_PAYLOAD_URL}${typeof campaignData.coverimage === 'string'
        ? campaignData.coverimage
        : campaignData.coverimage?.sizes?.medium?.url ?? ''
        }`
    }

    featuredCampaignsData.push({
      data: {
        type: 'home',
        sectionTitle: campaignData.title,
        sectionText: campaign.description || '',
        link: `${CANARY_MISSION.BASIC_URL}/campaign/${campaignData.slug}`,
        linkToShare: `${CANARY_MISSION.BASIC_URL}/campaign/${campaignData.slug}`,
        coverImage: coverImage || undefined,
        iframeType: iFrameType,
        iframeSrc: iFrameSrc,
        backgroundColor: campaign.backgroundColor?.backgroundColor || '#010231',
        darkBackgroundColor: campaign.darkBackgroundColor?.backgroundColor || '#010231',
      },
      profileList,
    })
  }

  return featuredCampaignsData
}

export const legacyProfileData = async (profile: Legacyprofile) => {
  const legacyProfileData =
    typeof profile === 'object' &&
    ({
      slug: profile.slug ?? '',
      fullName: profile.fullName ?? '',
      profileImage: profile.profileImage ?? '',
    } as IProfileLink)
  return legacyProfileData || null
}

export const publicProfileData = async (profile: Publicprofile) => {
  const publicProfileData =
    typeof profile === 'object' &&
    ({
      slug: profile.slug ?? '',
      fullName:
        typeof profile.profileEntity === 'string'
          ? profile.profileEntity
          : profile.profileEntity.fullName ?? '',
      profileImage: `${process.env.NEXT_PUBLIC_PAYLOAD_URL}${typeof profile.profileImage === 'string'
        ? profile.profileImage
        : profile.profileImage?.sizes?.thumbnail?.url ?? ''
        }`,
    } as IProfileLink)
  return publicProfileData || null
}

export const processProfilesFromBlock = async (
  block: HasManyProfileList,
  profileList: IProfileLink[],
): Promise<boolean> => {
  for (const profile of block || []) {
    if (profileList.length >= 30) return true

    let newProfile: IProfileLink | null = null

    if (
      profile.value &&
      typeof profile.value === 'object' &&
      profile.relationTo === 'publicprofiles'
    ) {
      newProfile = await publicProfileData(profile.value)
    }
    if (
      profile.value &&
      typeof profile.value === 'object' &&
      profile.relationTo === 'legacyprofiles'
    ) {
      newProfile = await legacyProfileData(profile.value)
    }
    if (
      newProfile &&
      !profileList.some((existingProfile) => existingProfile.slug === newProfile!.slug)
    ) {
      profileList.push(newProfile)
    }
  }
  return false
}
export const transformProfilesData = (featuredProfilesData: FeaturedProfilesData): TypesenseData[] => {
  const transformedData = [
    ...(featuredProfilesData?.publicProfiles
      ?.map((profile) => {
        if (typeof profile === 'object' && typeof profile.profileEntity === 'object') {
          return {
            slug: profile.slug,
            profileImage: `${process.env.NEXT_PUBLIC_PAYLOAD_URL}${typeof profile.profileImage === 'object'
              ? profile.profileImage?.sizes?.medium?.url
              : profile.profileImage
              }`,
            fullName: profile.profileEntity.fullName,
            organizations: profile.profileEntity.organizations,
            universities: profile.profileEntity.universities,
            region: profile.profileEntity.region,
            status: [profile.profileEntity.status],
            description: JSON.stringify(profile.overview),
            source: 'CMS',
            stories: profile.stories || [],
          }
        }
        return null
      })
      .filter(Boolean) as TypesenseData[]),
    ...(featuredProfilesData?.legacyProfiles
      ?.map((profile) => {
        if (typeof profile === 'object') {
          return {
            slug: profile.slug || '',
            profileImage: profile.profileImage || '',
            fullName: profile.fullName || '',
            organizations: profile.organizations?.map(org => ({ name: org.organization })) || [],
            universities: profile.universities || [],
            region: profile.region || '',
            state: profile.state || '',
            status: profile.professions?.map((p) => p.profession) || [],
            description: profile.description || '',
            source: 'MongoDB',
            stories: profile.stories || [],
          }
        }
        return null
      })
      .filter(Boolean) as TypesenseData[]),
  ]
  return transformedData;
}